import React, { useState, useMemo, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import Container from "../components/Container";
import translations from "../i18n/insightspage.json";
import faroLogo from "../assets/faro.png";

/* ─── Palette ─── */
const C = {
  navy: "#0B1F3A",
  navyLight: "#112B4D",
  navyMid: "#0E2545",
  amber: "#FFC857",
  amberDark: "#E6B04E",
  paper: "#F5F2EA",
  white: "#F5F7FA",
  gray: "#6B7280",
  grayLight: "#9CA3AF",
  grayDark: "#4B5563",
};

const SERIF = "'Fraunces', 'Playfair Display', Georgia, serif";
const SANS = "'Inter', 'DM Sans', system-ui, sans-serif";
const MONO = "'JetBrains Mono', 'IBM Plex Mono', ui-monospace, monospace";

const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  visible: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { delay: i * 0.04, duration: 0.45, ease: "easeOut" },
  }),
};

/* Region label color treatments on paper bg */
const regionStyle = {
  argentina: { bg: "rgba(37,99,235,0.10)", color: "#1D4ED8", border: "rgba(37,99,235,0.22)" },
  latam: { bg: "rgba(255,200,87,0.18)", color: C.amberDark, border: "rgba(224,176,78,0.42)" },
  global: { bg: "rgba(11,31,58,0.06)", color: C.navy, border: "rgba(11,31,58,0.15)" },
};

/* ─── primitives ─── */
function AmberSidebar({ opacity = 0.15 }) {
  return <div className="absolute top-0 right-0 w-2 md:w-3 h-full pointer-events-none"
    style={{ background: C.amber, opacity }} />;
}
function AmberRule({ width = "120px", className = "" }) {
  return <div className={`h-0.5 rounded-full ${className}`} style={{ width, background: C.amber }} />;
}
function FaroWatermark({ className = "", position = "left", opacity = 0.04 }) {
  return (
    <div className={`absolute pointer-events-none select-none ${className}`}
      style={{ transform: position === "right" ? "scaleX(-1)" : "none" }}>
      <img src={faroLogo} alt="" className="w-full h-full object-contain"
        style={{ opacity, filter: "brightness(2)" }} draggable={false} />
    </div>
  );
}

/* ─── Filter chip (toggleable) ─── */
function Chip({ label, active, onClick }) {
  return (
    <button onClick={onClick}
      className="rounded-full px-3 py-1 text-[11px] font-semibold transition"
      style={{
        background: active ? C.amber : C.white,
        color: active ? C.navy : C.grayDark,
        border: `1px solid ${active ? C.amberDark : "rgba(11,31,58,0.12)"}`,
        boxShadow: active ? `0 2px 8px rgba(255,200,87,0.25)` : "none",
        fontFamily: SANS,
      }}>
      {label}
    </button>
  );
}

/* ─── Filter dropdown panel ─── */
function FilterPanel({ t, filters, setFilters }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function close(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const hasFilters = filters.area || filters.angle || filters.region;
  const toggleArea = (k) => setFilters((f) => ({ ...f, area: f.area === k ? null : k }));
  const toggleAngle = (k) => setFilters((f) => ({ ...f, angle: f.angle === k ? null : k }));
  const toggleRegion = (k) => setFilters((f) => ({ ...f, region: f.region === k ? null : k }));
  const clearAll = () => setFilters({ area: null, angle: null, region: null });
  const en = t.filterLabel === "Filter";

  return (
    <div ref={ref} className="relative">
      {/* Trigger */}
      <button onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 rounded-full px-4 py-2 text-[13px] font-bold transition hover:-translate-y-0.5"
        style={{
          background: hasFilters ? C.amber : C.white,
          color: hasFilters ? C.navy : C.navy,
          border: `1px solid ${hasFilters ? C.amberDark : "rgba(11,31,58,0.15)"}`,
          fontFamily: SANS,
          boxShadow: hasFilters ? `0 4px 16px rgba(255,200,87,0.25)` : "0 1px 2px rgba(11,31,58,0.04)",
        }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
          <line x1="4" y1="6" x2="20" y2="6" /><line x1="8" y1="12" x2="20" y2="12" /><line x1="12" y1="18" x2="20" y2="18" />
        </svg>
        {t.filterLabel}
        {hasFilters && (
          <span className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold"
            style={{ background: C.navy, color: C.amber }}>
            {[filters.area, filters.angle, filters.region].filter(Boolean).length}
          </span>
        )}
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
          className={`transition-transform ${open ? "rotate-180" : ""}`}>
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {/* Panel */}
      {open && (
        <div className="absolute top-full left-0 mt-3 w-80 rounded-2xl p-5 z-50"
          style={{
            background: C.white,
            border: `1px solid rgba(255,200,87,0.4)`,
            boxShadow: "0 20px 60px rgba(11,31,58,0.18)",
          }}>
          {[
            { kicker: en ? "Area" : "Área", entries: t.areas, sel: filters.area, toggle: toggleArea },
            { kicker: en ? "Angle" : "Ángulo", entries: t.angles, sel: filters.angle, toggle: toggleAngle },
            { kicker: en ? "Region" : "Región", entries: t.regions, sel: filters.region, toggle: toggleRegion },
          ].map((g) => (
            <div key={g.kicker} className="mb-4 last:mb-0">
              <span className="block text-[10px] font-bold uppercase tracking-[0.22em] mb-2.5"
                style={{ color: C.amberDark, fontFamily: MONO }}>
                {g.kicker}
              </span>
              <div className="flex flex-wrap gap-1.5">
                {Object.entries(g.entries).map(([k, label]) => (
                  <Chip key={k} label={label} active={g.sel === k} onClick={() => g.toggle(k)} />
                ))}
              </div>
            </div>
          ))}
          {hasFilters && (
            <button onClick={clearAll}
              className="text-[11px] font-bold uppercase tracking-[0.18em] mt-2 transition hover:opacity-80"
              style={{ color: C.amberDark, fontFamily: MONO }}>
              {t.clearFilters}
            </button>
          )}
        </div>
      )}

      {/* Active inline pills */}
      {hasFilters && !open && (
        <div className="absolute left-full top-1/2 -translate-y-1/2 ml-2 hidden md:flex items-center gap-1.5">
          {filters.area && (
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ background: "rgba(255,200,87,0.18)", color: C.amberDark, border: `1px solid rgba(224,176,78,0.32)` }}>
              {t.areas[filters.area]}
            </span>
          )}
          {filters.angle && (
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ background: "rgba(255,200,87,0.18)", color: C.amberDark, border: `1px solid rgba(224,176,78,0.32)` }}>
              {t.angles[filters.angle]}
            </span>
          )}
          {filters.region && (
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ background: "rgba(255,200,87,0.18)", color: C.amberDark, border: `1px solid rgba(224,176,78,0.32)` }}>
              {t.regions[filters.region]}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

export default function InsightsPage({ language }) {
  const t = translations[language];
  const en = language === "en";
  const [filters, setFilters] = useState({ area: null, angle: null, region: null });

  const filtered = useMemo(() => {
    return t.articles.filter((a) => {
      if (filters.area && !a.areas.includes(filters.area)) return false;
      if (filters.angle && a.angle !== filters.angle) return false;
      if (filters.region && a.region !== filters.region) return false;
      return true;
    });
  }, [t.articles, filters]);

  return (
    <>
      {/* ═══ 1. HERO — navy con título grande y acento serif ═══ */}
      <section className="relative pt-20 md:pt-28 pb-14 md:pb-16 overflow-hidden"
        style={{ background: `linear-gradient(180deg, ${C.navy} 0%, ${C.navyMid} 100%)` }}>
        <div className="absolute top-0 left-0 w-full h-1" style={{ background: C.amber }} />
        <AmberSidebar />
        <FaroWatermark className="-top-10 -right-10 w-[50%] h-[110%]" position="right" />
        <Container className="relative z-10">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
            className="max-w-3xl mx-auto text-center">
            <span className="inline-block text-[11px] md:text-[12px] font-bold uppercase tracking-[0.32em] mb-4"
              style={{ color: C.amber, fontFamily: MONO }}>
              {t.kicker}
            </span>
            <h1 className="text-[34px] md:text-[44px] lg:text-[52px] font-bold leading-[1.1]"
              style={{ color: C.white, fontFamily: SANS, letterSpacing: "-0.015em" }}>
              {t.title.split(" ").slice(0, -1).join(" ")}{" "}
              <span style={{ color: C.amber, fontFamily: SERIF, fontStyle: "italic", fontWeight: 500 }}>
                {t.title.split(" ").slice(-1)[0]}
              </span>
            </h1>
            <AmberRule className="mt-6 mx-auto" />
            <p className="mt-5 text-[15px] md:text-base leading-[1.7] max-w-2xl mx-auto" style={{ color: C.grayLight }}>
              {t.subtitle}
            </p>
          </motion.div>
        </Container>
        <div className="absolute bottom-0 left-0 w-full h-1" style={{ background: C.amber, opacity: 0.3 }} />
      </section>

      {/* ═══ 2. FILTER BAR + ARTICLES — fondo paper ═══ */}
      <section className="relative py-12 md:py-16 overflow-hidden" style={{ background: C.paper }}>
        {/* paper grain */}
        <div className="absolute inset-0 pointer-events-none opacity-[0.35]" style={{
          backgroundImage: `radial-gradient(rgba(11,31,58,0.04) 1px, transparent 1px)`,
          backgroundSize: "3px 3px",
        }} />
        <Container className="relative z-10">
          {/* Filter bar */}
          <div className="flex items-center gap-3 mb-8">
            <FilterPanel t={t} filters={filters} setFilters={setFilters} />
            <span className="ml-auto text-[11px] font-bold uppercase tracking-[0.22em]"
              style={{ color: C.amberDark, fontFamily: MONO }}>
              {filtered.length} {en ? "articles" : "artículos"}
            </span>
          </div>

          {/* Articles grid */}
          <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3 max-w-6xl mx-auto">
            {filtered.map((article, i) => {
              const rs = regionStyle[article.region] || regionStyle.global;
              return (
                <motion.a
                  key={article.id}
                  href={article.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  custom={i}
                  variants={fadeUp}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, margin: "-40px" }}
                  className="group relative flex flex-col rounded-2xl p-6 transition-all hover:-translate-y-1 overflow-hidden"
                  style={{
                    background: C.white,
                    border: `1px solid rgba(11,31,58,0.08)`,
                    boxShadow: "0 1px 3px rgba(0,0,0,0.04), 0 6px 18px rgba(11,31,58,0.05)",
                  }}
                >
                  {/* Hover amber accent strip on top */}
                  <div className="absolute top-0 left-0 right-0 h-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                    style={{ background: C.amber }} />

                  {/* Source · year + region */}
                  <div className="flex items-center justify-between gap-2 mb-4">
                    <span className="text-[11px] font-bold uppercase tracking-[0.18em] truncate"
                      style={{ color: C.amberDark, fontFamily: MONO }}>
                      {article.source} · {article.year}
                    </span>
                    <span className="flex-shrink-0 text-[10px] font-bold px-2 py-0.5 rounded-full"
                      style={{ background: rs.bg, color: rs.color, border: `1px solid ${rs.border}` }}>
                      {t.regions[article.region]}
                    </span>
                  </div>

                  {/* Title */}
                  <h3 className="text-[15.5px] font-bold leading-snug mb-3 transition-colors group-hover:text-amber-700"
                    style={{ color: C.navy, fontFamily: SANS }}>
                    {article.title}
                  </h3>

                  {/* Insight */}
                  <p className="text-[13px] leading-[1.65] mb-5 flex-1" style={{ color: C.grayDark }}>
                    {article.insight}
                  </p>

                  {/* Area pills + read more */}
                  <div className="flex items-end justify-between gap-3 pt-4"
                    style={{ borderTop: "1px solid rgba(11,31,58,0.08)" }}>
                    <div className="flex flex-wrap gap-1">
                      {article.areas.map((a) => (
                        <span key={a} className="text-[10px] font-semibold px-2 py-0.5 rounded-full"
                          style={{ background: "rgba(11,31,58,0.05)", color: C.grayDark }}>
                          {t.areas[a]}
                        </span>
                      ))}
                    </div>
                    <span className="flex-shrink-0 text-[12px] font-bold uppercase tracking-[0.14em] transition-colors group-hover:text-amber-700"
                      style={{ color: C.navy, fontFamily: MONO }}>
                      {t.readMore} →
                    </span>
                  </div>
                </motion.a>
              );
            })}
          </div>

          {filtered.length === 0 && (
            <p className="text-center py-16 text-sm" style={{ color: C.grayDark }}>
              {t.noResults}
            </p>
          )}
        </Container>
      </section>
    </>
  );
}
